import { withAutoScrollable } from '@src/components/base/AutoScrollable'
import { clearActiveCommentId, setActiveEntityKey } from '@src/state/document/actions'
import {
  isGatedEntity,
  selectEntityType,
  selectHighlightColor,
  selectIsFiltered,
  selectKeyForFocused,
} from '@src/state/document/selectors'
import { RootState } from '@src/state/types'
import { rgba } from 'polished'
import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import styled from 'styled-components'

const HighlightWrapper = styled.span<HighlightProps>`
  border-bottom: ${({ filtered, isGated, focusedComponent, focused, color }) => {
    if (!filtered || isGated) {
      return 'none'
    }

    if (focusedComponent && !focused) {
      return `3px solid ${rgba(color, 0.4)}`
    }

    return `3px solid ${color}`
  }};
  background-color: ${props => props.focused && props.filtered && rgba(props.color, 0.2)};
  padding-top: 4px;
  &.active-token,
  &:hover {
    background-color: ${props =>
      props.filtered && !props.isGated ? rgba(props.color, 0.2) : 'transparent'};
  }
`

const FOCUSED = 'editor'

export interface TokenKeyProps {
  entityKey: string
  decoratedText: string
}

interface BaseHighlightProps {
  focused: boolean
  filtered: boolean
  focusedComponent: string | undefined
  color: string
  type: string
  isGated?: boolean
}

interface HighlightProps extends BaseHighlightProps {
  onMouseOver: () => void
  onMouseOut: () => void
  onClick: () => void
}

interface DispatchProps {
  clearActiveCardKey: () => void
  setActiveEntityKey: (key: string, isClick?: boolean, type?: string) => void
}

type Props = BaseHighlightProps & DispatchProps & TokenKeyProps

export class Highlight extends React.Component<Props, { clicked: boolean }> {
  public state = { clicked: false }

  public shouldComponentUpdate(nextProps: Props) {
    const {
      color,
      focused,
      entityKey,
      decoratedText,
      filtered,
      isGated,
      focusedComponent,
    } = this.props

    return (
      filtered !== nextProps.filtered ||
      color !== nextProps.color ||
      focused !== nextProps.focused ||
      entityKey !== nextProps.entityKey ||
      decoratedText !== nextProps.decoratedText ||
      isGated !== nextProps.isGated ||
      focusedComponent !== nextProps.focusedComponent
    )
  }

  public render() {
    const { focused, focusedComponent } = this.props
    const Scrollable = withAutoScrollable(HighlightWrapper)
    const scroll = focused && focusedComponent !== FOCUSED

    return (
      <Scrollable
        behavior={focusedComponent === 'mobile' ? 'auto' : 'smooth'}
        block="center"
        inline="center"
        shouldScroll={scroll}
        onClick={this.handleClick}
        onMouseOut={this.onMouseOut}
        onMouseOver={this.onMouseOver}
        data-test={`highlight-${this.props.decoratedText}`}
        {...this.props}
      >
        {this.props.children}
      </Scrollable>
    )
  }

  private handleClick = async () => {
    const { entityKey, type, filtered, isGated } = this.props
    this.setState({ clicked: true })
    if (filtered && !isGated) {
      this.props.setActiveEntityKey(entityKey, true, type)
    }
  }

  private onMouseOver = () => {
    const { filtered, entityKey, isGated } = this.props

    if (filtered && !isGated) {
      this.props.setActiveEntityKey(entityKey)
    }
  }

  private onMouseOut = async () => {
    const { filtered } = this.props
    if (this.state.clicked) {
      return this.setState({ clicked: false })
    }
    if (filtered) {
      this.props.clearActiveCardKey()
    }
  }
}

function mapStateToProps(state: RootState, props: TokenKeyProps): BaseHighlightProps {
  return {
    color: selectHighlightColor(state),
    type: selectEntityType(state),
    focused: selectKeyForFocused(state, props),
    filtered: selectIsFiltered(state, props),
    focusedComponent: state.workspace.focusedComponent,
    isGated: isGatedEntity(state, props),
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    clearActiveCardKey: () => dispatch(clearActiveCommentId()),
    setActiveEntityKey: (key: string, isClick: boolean = false, type: string) =>
      dispatch(setActiveEntityKey({ key, component: FOCUSED }, { isClick, type })),
  }
}

export default connect<BaseHighlightProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(Highlight)



// WEBPACK FOOTER //
// ./src/components/Editor/Highlight.tsx